import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import BottomNav from '../components/BottomNav';
import { readThroughLabel } from '../utils/formatUtils';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { getProfile, startNewReadThrough } = useApi();

  const [currentReadThrough, setCurrentReadThrough] = useState<number>(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    getProfile().then((p) => {
      if (p?.currentReadThrough) setCurrentReadThrough(p.currentReadThrough);
    }).catch(() => {});
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleStartNewReadThrough = async () => {
    setIsStarting(true);
    try {
      await startNewReadThrough(currentReadThrough);
      setCurrentReadThrough((n) => n + 1);
      setShowConfirm(false);
    } catch {
      // silently ignore
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment-50 pb-20">
      {/* Header */}
      <div className="bg-parchment-100 border-b border-parchment-200 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <span className="text-2xl">📖</span>
          <h1 className="text-xl font-bold text-ink">TrackBible</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8 flex flex-col gap-6">
        <div>
          <h2 className="text-2xl font-bold text-ink">Profile</h2>
        </div>

        {/* Account card */}
        <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-6 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-parchment-300 flex items-center justify-center text-2xl shrink-0">
              👤
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">Signed in as</p>
              <p className="text-sm text-ink-muted truncate">{user?.signInDetails?.loginId ?? 'Unknown'}</p>
            </div>
          </div>
        </div>

        {/* Read-through card */}
        <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm p-6 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-ink">Bible Read-Through</p>
              <p className="text-sm text-ink-muted mt-0.5">
                Currently on your{' '}
                <span className="font-semibold text-leather">{readThroughLabel(currentReadThrough)}</span>{' '}
                read
              </p>
            </div>
            <span className="text-2xl">📖</span>
          </div>
          <button
            onClick={() => setShowConfirm(true)}
            className="w-full py-2.5 rounded-lg border-2 border-leather text-leather font-semibold text-sm hover:bg-parchment-200 active:scale-95 transition"
          >
            Start New Read-Through
          </button>
        </div>

        {/* Confirmation dialog */}
        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-parchment-50 rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4">
              <h3 className="text-lg font-bold text-ink">Start a new read-through?</h3>
              <p className="text-sm text-ink-muted">
                This will begin read-through #{currentReadThrough + 1}, starting from Genesis 1.
                Your previous progress is saved and can be viewed on the Progress page.
              </p>
              <div className="flex gap-3 mt-1">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 py-2.5 rounded-lg bg-parchment-200 hover:bg-parchment-300 text-ink font-semibold text-sm transition"
                >
                  Cancel
                </button>
                <button
                  disabled={isStarting}
                  onClick={handleStartNewReadThrough}
                  className="flex-1 py-2.5 rounded-lg bg-leather hover:bg-leather-light text-parchment-100 font-semibold text-sm transition disabled:opacity-50"
                >
                  {isStarting ? 'Starting…' : 'Yes, Start'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sign out */}
        <div className="bg-parchment-100 rounded-xl border border-parchment-200 shadow-sm divide-y divide-parchment-200">
          <button
            onClick={handleSignOut}
            className="w-full text-left px-6 py-4 text-sm font-semibold text-red-600 hover:bg-parchment-200 rounded-xl transition"
          >
            Sign Out
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ProfilePage;
